import { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { IoClose } from "react-icons/io5";

import {
  Drawer,
  Button,
  IconButton,
  Select,
  Option,
  Input,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@material-tailwind/react";

import { IoRemoveCircleOutline } from "react-icons/io5";
import { SeoTags } from "..";
import { useBusinessContext } from "../../contexts/BusinessDetailsContextProvider";
import allCountryCodes from "../../data/countryAndCodes.json";
import {
  phoneNumberSanitation,
  contactNameSanitation,
} from "../utils/inputSanitation.js";

const BusinessCardDetails = () => {
  const {
    getBusinessCards,
    addCompRepresentative,
    RemoveCompRepresentative,
    getCompanyRepresentatives,
  } = useBusinessContext();
  const [businessCards, setBusinessCards] = useState([]);
  const [toCreateCompRep, setToCreateCompRep] = useState(false);
  const [countryCode, setCountryCode] = useState("91");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [name, setName] = useState("");
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [companyRepresentativeId, setCompanyRepresentativeId] = useState("");
  const [companyRepresentatives, setCompanyRepresentatives] = useState([]);

  const companyRepresentativesTableColumns = [
    {
      field: "name",
      headerName: "Name",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.name,
    },
    {
      field: "countryCode",
      headerName: "Country Code",
      width: 140,
      sortable: true,
      valueGetter: (value, row) => row?.countryCode,
    },
    {
      field: "phoneNumber",
      headerName: "Phone Number",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.phoneNumber,
    },
    {
      field: "_id",
      headerName: "Action",
      width: 80,
      sortable: true,
      renderCell: (params) => (
        <IconButton
          onClick={() => {
            setCompanyRepresentativeId(params?.row?._id);
            setIsRemoveDialogOpen(true);
          }}
        >
          <IoRemoveCircleOutline />
        </IconButton>
      ),
    },
  ];

  const businessCardsTableColumns = [
    {
      field: "contactName",
      headerName: "Contact Name",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.name,
    },
    {
      field: "contactDesignation",
      headerName: "Designation",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.designation,
    },
    {
      field: "phone1",
      headerName: "Phone 1",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.phones?.[0] || "",
    },
    {
      field: "phone2",
      headerName: "Phone 2",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.phones?.[1] || "",
    },
    {
      field: "whatsappNo",
      headerName: "Whatsapp No.",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.whatsapp,
    },
    {
      field: "email1",
      headerName: "Email 1",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.emails?.[0] || "",
    },
    {
      field: "email2",
      headerName: "Email 2",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.emails?.[1] || "",
    },
    {
      field: "website1",
      headerName: "Website 1",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.websites?.[0] || "",
    },
    {
      field: "website2",
      headerName: "Website 2",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.websites?.[1] || "",
    },
    {
      field: "description",
      headerName: "Description",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.description,
    },
    {
      field: "contactRemarks",
      headerName: "Remarks",
      width: 200,
      sortable: true,
      valueGetter: (value, row) => row?.remarks,
    },
  ];

  const addCompanyRepresentative = async (e) => {
    e.preventDefault();
    const response = await addCompRepresentative(
      phoneNumber,
      countryCode,
      name
    );
    if (response) {
      setToCreateCompRep(false);
      const companyRepresentatives = await getCompanyRepresentatives();
      setCompanyRepresentatives(companyRepresentatives);
    }
  };

  const removeCompanyRepresentative = async (executiveId) => {
    const response = await RemoveCompRepresentative(executiveId);
    if (response) {
      const companyRepresentatives = await getCompanyRepresentatives();
      setCompanyRepresentatives(companyRepresentatives);
    }
  };

  const fetchAllBusinessCards = async () => {
    const response = await getBusinessCards();
    setBusinessCards(response);
  };

  const exportBusinessCards = () => {
    const flattenedData = businessCards.map((row) => ({
      "Contact Name": row?.name || "",
      Designation: row?.designation || "",
      "Phone 1": row?.phones?.[0] || "",
      "Phone 2": row?.phones?.[1] || "",
      "Whatsapp No.": row?.whatsapp || "",
      "Email 1": row?.emails?.[0] || "",
      "Email 2": row?.emails?.[1] || "",
      "Website 1": row?.websites?.[0] || "",
      "Website 2": row?.websites?.[1] || "",
      Description: row?.description || "",
      Remarks: row?.remarks || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(flattenedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BusinessCards");
    XLSX.writeFile(workbook, "BusinessCards.xlsx");
  };

  useEffect(() => {
    fetchAllBusinessCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="w-full mt-5 mb-10 lg:my-1 space-y-5">
      <SeoTags title="Business Card ‣ WaBM" />
      <div className="w-full space-y-2">
        <h1 className="text-2xl">Business Card</h1>
        <h3>Get the list of all the business cards detected on your WaBM</h3>
      </div>
      <div className="wbm-card-3 border-b-0 space-y-5">
        <div className="flex flex-row justify-start gap-2 items-center">
          <Button
            color="blue"
            size="sm"
            disabled={businessCards?.length < 1}
            onClick={exportBusinessCards}
          >
            Export to Excel
          </Button>
          <Button
            color="blue"
            size="sm"
            onClick={async () => {
              const companyRepresentatives = await getCompanyRepresentatives();
              setCompanyRepresentatives(companyRepresentatives);
              setToCreateCompRep(true);
              setPhoneNumber("");
              setName("");
            }}
          >
            Company Representative
          </Button>
        </div>
        <Paper style={{ position: "relative", zIndex: 1, height: 550 }}>
          <DataGrid
            getRowId={(row) => row?._id}
            rows={businessCards}
            columns={businessCardsTableColumns}
            initialState={{
              pagination: { paginationModel: { pageSize: 100 } },
            }}
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "background.paper",
              },
              "& .MuiDataGrid-virtualScroller": {
                marginTop: "0px",
              },
            }}
            getRowClassName={() => "cursor-pointer"}
            disableColumnMenu={false}
            disableRowSelectionOnClick={true}
          />
        </Paper>
      </div>

      <Dialog
        open={isRemoveDialogOpen}
        onClose={() => setIsRemoveDialogOpen(false)}
      >
        <DialogHeader>Remove Company Representative?</DialogHeader>
        <DialogBody className="space-y-2">
          Do you want to remove this company representative?
        </DialogBody>
        <DialogFooter className="space-x-3">
          <Button
            variant="gradient"
            color="red"
            onClick={() => setIsRemoveDialogOpen(false)}
          >
            No
          </Button>
          <Button
            variant="gradient"
            color="green"
            onClick={async () => {
              await removeCompanyRepresentative(companyRepresentativeId);
              setIsRemoveDialogOpen(false);
            }}
            autoFocus
          >
            Yes
          </Button>
        </DialogFooter>
      </Dialog>

      <Drawer
        placement="right"
        open={toCreateCompRep}
        size="700px"
        className="w-full p-5 space-y-3"
      >
        <div className="flex flex-row justify-between items-center">
          <h2 className="text-xl">Add Company Representative</h2>
          <button
            onClick={() => setToCreateCompRep(false)}
            className="my-button-style-cancel right-5"
          >
            <IoClose size={18} />
          </button>
        </div>

        <form
          onSubmit={(e) => {
            addCompanyRepresentative(e);
          }}
          className="wbm-card-3 flex flex-col space-y-5"
        >
          <Input
            label="Name"
            variant="standard"
            color="blue"
            labelProps={{
              className: "text-slate-800",
            }}
            value={name}
            onChange={(e) => setName(contactNameSanitation(e.target.value))}
            className="my-input-box-1"
          />
          <span className="w-full flex gap-3">
            <Select
              label="Country Code"
              variant="standard"
              color="blue"
              required
              value={countryCode}
              onChange={(e) => setCountryCode(e)}
              className="my-input-box-1"
            >
              {allCountryCodes.map((country) => (
                <Option
                  key={country.code}
                  value={country.dial_code}
                  className={`hover:bg-blue-300 focus:bg-blue-300`}
                >
                  {country.code + " +" + country.dial_code}
                </Option>
              ))}
            </Select>
            <Input
              label="Phone Number"
              variant="standard"
              color="blue"
              labelProps={{
                className: "text-slate-800",
              }}
              required
              value={phoneNumber}
              onChange={(e) =>
                setPhoneNumber(phoneNumberSanitation(e.target.value))
              }
              className="my-input-box-1"
            />
          </span>
          <Button
            type="submit"
            variant="outlined"
            color="green"
            className="w-fit"
          >
            Add Representative
          </Button>
        </form>

        <div className="wbm-card-3 flex flex-col w-full justify-start">
          <DataGrid
            getRowId={(row) => row?._id}
            rows={companyRepresentatives}
            columns={companyRepresentativesTableColumns}
            initialState={{
              pagination: { paginationModel: { pageSize: 100 } },
            }}
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "background.paper",
              },
              "& .MuiDataGrid-virtualScroller": {
                marginTop: "0px",
              },
            }}
            getRowClassName={() => "cursor-pointer"}
            disableColumnMenu={false}
            disableRowSelectionOnClick={true}
          />
        </div>
      </Drawer>
    </main>
  );
};

export default BusinessCardDetails;
